import { tempoLeitura } from "@/lib/blog/leitura";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";

import type { Database } from "@/integrations/supabase/types";

export interface BlogCategory {
  slug: string;
  nome: string;
  ordem: number;
}

export interface BlogPostCard {
  slug: string;
  titulo: string;
  subtitulo: string | null;
  capa_url: string | null;
  categoria_slugs: string[];
  published_at: string;
  /** Tempo estimado de leitura (só nas listagens). */
  minutos?: number;
}

export interface BlogPostFull extends BlogPostCard {
  autor: string | null;
  autor_cidade: string | null;
  conteudo_md: string;
  seo_title: string | null;
  seo_description: string | null;
  updated_at: string;
}

function publicClient() {
  return createClient<Database>(
    process.env["SUPABASE_URL"]!,
    process.env["SUPABASE_PUBLISHABLE_KEY"]!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

const CARD_COLUMNS = "slug, titulo, subtitulo, capa_url, categoria_slugs, published_at";

/** Home e listagens: todos os posts publicados (42), ordenados por data desc. */
export const getBlogIndex = createServerFn({ method: "GET" }).handler(async () => {
  const supabase = publicClient();

  const [postsRes, catsRes] = await Promise.all([
    supabase
      .from("blog_posts")
      .select(`${CARD_COLUMNS}, conteudo_md`)
      .eq("status", "published")
      .order("published_at", { ascending: false }),
    supabase.from("blog_categories").select("slug, nome, ordem").order("ordem"),
  ]);

  if (postsRes.error) throw new Error(postsRes.error.message);
  if (catsRes.error) throw new Error(catsRes.error.message);

  return {
    posts: (postsRes.data ?? []).map(({ conteudo_md, ...p }) => ({
      ...p,
      minutos: tempoLeitura(conteudo_md).minutos,
    })) as BlogPostCard[],
    categories: (catsRes.data ?? []) as BlogCategory[],
  };
});

/** Artigo por slug + relacionados da mesma categoria. `null` quando não existe. */
export const getBlogPost = createServerFn({ method: "GET" })
  .inputValidator((data: { slug: string }) => ({ slug: String(data.slug) }))
  .handler(async ({ data }) => {
    const supabase = publicClient();

    const { data: post, error } = await supabase
      .from("blog_posts")
      .select(
        `${CARD_COLUMNS}, autor, autor_cidade, conteudo_md, seo_title, seo_description, updated_at`,
      )
      .eq("status", "published")
      .eq("slug", data.slug)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!post) return { post: null, related: [] as BlogPostCard[], categories: [] as BlogCategory[] };

    const typed = post as unknown as BlogPostFull;
    const mainCategory = typed.categoria_slugs?.[0];

    const [relatedRes, catsRes] = await Promise.all([
      mainCategory
        ? supabase
            .from("blog_posts")
            .select(CARD_COLUMNS)
            .eq("status", "published")
            .contains("categoria_slugs", [mainCategory])
            .neq("slug", typed.slug)
            .order("published_at", { ascending: false })
            .limit(3)
        : Promise.resolve({ data: [], error: null }),
      supabase.from("blog_categories").select("slug, nome, ordem").order("ordem"),
    ]);

    if (relatedRes.error) throw new Error(relatedRes.error.message);
    if (catsRes.error) throw new Error(catsRes.error.message);

    // "Leia também" sempre com 3 artigos: completa com os mais recentes de outras áreas.
    let related = (relatedRes.data ?? []) as BlogPostCard[];
    if (related.length < 3) {
      const extra = await supabase
        .from("blog_posts")
        .select(CARD_COLUMNS)
        .eq("status", "published")
        .neq("slug", typed.slug)
        .order("published_at", { ascending: false })
        .limit(10);
      const vistos = new Set(related.map((r) => r.slug));
      for (const p of (extra.data ?? []) as BlogPostCard[]) {
        if (related.length >= 3) break;
        if (!vistos.has(p.slug)) related = [...related, p];
      }
    }

    return {
      post: typed,
      related,
      categories: (catsRes.data ?? []) as BlogCategory[],
    };
  });
